import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import {
  DataSource,
  DataSourcesService,
} from '../../../services/data-sources.service';
import {
  DozentBot,
  Tutor,
  TutorService,
} from '../../../services/tutor.service';
import {
  ConnectedLmsPlatforms,
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
} from '../../../services/lms-registry.service';
import { AuthService } from '../../../services/auth.service';
import { DozentService } from '../../../services/dozent.service';
import { LtiPlacementDialogCourse } from '../../../components/dialogs/lti-placement-dialog/lti-placement-dialog.component';
import { LtiConnectDialogComponent } from '../../../components/dialogs/lti-connect-dialog/lti-connect-dialog.component';

@Component({
  selector: 'app-placements-overview',
  templateUrl: './placements-overview.component.html',
  styleUrl: './placements-overview.component.scss',
})
export class PlacementsOverviewComponent implements OnInit, OnDestroy {
  /** Moodle plugin sites the client has registered. Usually exactly
   *  one — the UI degrades gracefully for 0 (CTA) and renders all if
   *  there happen to be more. */
  lmsSites: LmsRegistration[] = [];
  lmsSitesLoading = true;
  selectedMoodleRegistrationId: string | null = null;

  // ─── Tab 3 (LTI 1.3) ─────────────────────────────────────────────
  /** LTI registrations (`source='lti13'`). Each renders one
   *  `<app-lti-placements-card>`; the card loads its own placements. */
  ltiRegistrations: LmsRegistration[] = [];
  ltiLoading = true;
  /** AlphaTutor courses for the placement dialog's optional course
   *  binding. Loaded once, shared by every card. */
  ltiCourses: LtiPlacementDialogCourse[] = [];
  /** `?tab=lti` / `?tab=ilias` öffnet die passende Lasche direkt — aus dem
   *  Wizard, der Sidebar und der Integrationskarte. */
  initialTab: 'lms' | 'ilias' | 'lti' = 'lms';

  /** Wo Paket und Anleitung des jeweiligen Plugins liegen.
   *
   *  Feste Adressen statt i18n-Werte: eine URL ist keine Uebersetzung, und
   *  zwei Sprachstaende, die auseinanderlaufen, waeren zwei kaputte Links.
   *  Beide Repositorien sind oeffentlich. */
  readonly moodlePluginUrl =
    'https://github.com/alphabees-25/moodle-block_alphabees/releases/latest';
  readonly moodlePluginGuideUrl =
    'https://github.com/alphabees-25/moodle-block_alphabees#readme';
  readonly iliasPluginUrl =
    'https://github.com/alphabees-25/ilias-plugin-alphabees/releases/latest';
  readonly iliasPluginGuideUrl =
    'https://github.com/alphabees-25/ilias-plugin-alphabees#installation';

  /** Welche LMS angebunden sind. Steuert nur noch den Inhalt der Laschen,
   *  nicht mehr ihre Existenz: ohne Verbindung standen frueher weder Moodle
   *  noch ILIAS da, und der einzige Einstieg fuehrte ueber LTI — also ueber
   *  den Weg, den wir zuletzt empfehlen. */
  connectedLms: ConnectedLmsPlatforms = {
    moodle: false, ilias: false, lti: false, otherLti: false,
  };

  /** Steht die Liste der angebundenen Plattformen? Erst dann steht fest,
   *  welche Laschen es gibt. */
  platformsLoaded = false;

  /** Die Laschen werden erst gebaut, wenn feststeht, welche es gibt.
   *
   *  `NbTabsetComponent` wählt die aktive Lasche genau einmal in
   *  `ngAfterContentInit` (`tabs.find(tab => tab.active) || tabs.first`) und
   *  fasst danach nur noch bei einem Routenwechsel an. Eine Lasche, die
   *  später durch ein `*ngIf` dazukommt, bringt ihr eigenes `[active]` mit,
   *  ohne dass der bereits aktiven eines weggenommen wird — dann leuchten
   *  zwei gleichzeitig. Genau das passierte hier: vor der Antwort von
   *  `getConnectedPlatforms()` ist `connectedLms.ilias` falsch, also gab es
   *  zuerst nur „LTI 1.3", und die Moodle-Lasche kam mit `active` nach. */
  tabsReady = false;

  /** ILIAS-Verbindungen. Getrennt von den übrigen LTI-Verbindungen, weil
   *  ILIAS über SOAP eine Kursliste mitbringt und die anderen nicht. */
  get iliasRegistrations(): LmsRegistration[] {
    return this.ltiRegistrations.filter((r) => LmsRegistryService.isIlias(r));
  }

  /** LTI-Verbindungen, die kein ILIAS sind — Canvas, Brightspace, Open edX. */
  get otherLtiRegistrations(): LmsRegistration[] {
    return this.ltiRegistrations.filter((r) => !LmsRegistryService.isIlias(r));
  }

  /** Die generische LTI-Lasche erscheint für Fremd-LMS und immer dann, wenn
   *  gar nichts angebunden ist — sonst gäbe es keinen Einstieg zum
   *  Einrichten. */
  get showGenericLtiTab(): boolean {
    return this.otherLtiRegistrations.length > 0 || !this.connectedLms.ilias;
  }

  /** Prüft nach jedem Ladevorgang, ob die Laschen jetzt feststehen. Einmal
   *  gesetzt, bleibt `tabsReady` gesetzt — `openLtiConnect` lädt die
   *  Registrierungen nach Dialogschluss neu, und dabei soll der Tabset nicht
   *  abgerissen und die gewählte Lasche nicht zurückgesetzt werden. */
  private markTabsReady(): void {
    if (this.platformsLoaded && !this.ltiLoading) {
      this.tabsReady = true;
    }
  }
  /** Einstiegskacheln des Leerzustands. Spiegelt `LtiConnectDialogComponent`
   *  — alle drei öffnen denselben Dialog, der Klick wählt nur den Einstieg. */
  readonly ltiChoices = [
    { key: 'moodle', image: '/assets/images/integrations/moodle.png', beta: false },
    { key: 'ilias', image: '/assets/images/integrations/ilias.png', beta: true },
    { key: 'other', image: '/assets/images/integrations/lti.svg', beta: false },
  ];

  // ─── Tab 2 (Schnellkonfiguration) ────────────────────────────────
  /** Cached for the embedded `<app-tutor-kb-row>` rows. The row
   *  component takes these as Inputs only — the parent owns loading. */
  agentTutors: Tutor[] = [];
  agentDataSources: DataSource[] = [];
  agentDozentBots: DozentBot[] = [];
  agentSearch = '';
  agentPage = 0;
  agentPageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];

  /** Placements indexed by bot_id so each row can compute its own
   *  read-only "X of Y courses synced" badge. */
  placementsByBotId: Map<string, LmsPlacement[]> = new Map();
  /** DataSource ids that correspond to a registration's `auto_bucket_id`.
   *  Displayed with a Kurs-Sync marker in the quick-config KB picker. */
  autoBucketDataSourceIds: Set<string> = new Set();

  private destroy$ = new Subject<void>();

  constructor(
    private tutorService: TutorService,
    private dataSourcesService: DataSourcesService,
    private lmsRegistry: LmsRegistryService,
    private authService: AuthService,
    private dozentService: DozentService,
    private route: ActivatedRoute,
    private dialogService: NbDialogService,
    private t: TranslateService,
  ) {}

  /** Öffnet den Verbindungsdialog und lädt danach neu — eine frisch
   *  eingerichtete Verbindung soll ohne Seitenwechsel auftauchen. */
  openLtiConnect(): void {
    this.dialogService
      .open(LtiConnectDialogComponent, { hasScroll: true, closeOnBackdropClick: true })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadLtiRegistrations());
  }

  /** Placement rows carry the client_id in `course_id` as a "no course
   *  bound" placeholder (Moodle inbound + LTI). Cards need it to tell
   *  that apart from a real course. */
  get clientId(): string | null {
    const payload = this.authService.accessTokenPayload$.value;
    return payload?.client_id ? String(payload.client_id) : null;
  }

  ngOnInit(): void {
    this.tutorService.listTutors().pipe(takeUntil(this.destroy$)).subscribe();
    this.dataSourcesService.listDataSources().pipe(takeUntil(this.destroy$)).subscribe();

    this.tutorService.tutors$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ts) => (this.agentTutors = ts));
    this.dataSourcesService.dataSources$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ds) => {
        this.agentDataSources = ds;
        // Sites may have resolved first; re-derive the auto-bucket
        // DataSource-id set so the Schnellkonfig dropdown filters it
        // out even when dataSources finishes loading after registrations.
        this.rebuildAutoBucketDataSourceIds();
      });
    this.tutorService
      .listDozentBots()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (bots) => (this.agentDozentBots = bots),
        error: () => (this.agentDozentBots = []),
      });

    this.loadLmsSites();
    this.loadLtiRegistrations();

    this.lmsRegistry
      .getConnectedPlatforms()
      .pipe(takeUntil(this.destroy$), catchError(() => of(this.connectedLms)))
      .subscribe((platforms) => {
        this.connectedLms = platforms;
        this.platformsLoaded = true;
        this.markTabsReady();
      });

    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'lti' || tab === 'ilias') {
      this.initialTab = tab;
    }
  }

  private loadLtiRegistrations(): void {
    this.ltiLoading = true;
    // Zwei Quellen, weil es zwei Arten gibt, an ILIAS zu haengen: ueber LTI
    // und, seit Migration 158, allein ueber unser Plugin. Eine reine
    // Plugin-Verbindung hat `source='ilias_plugin'` und faellt aus dem
    // `lti13`-Abruf heraus — sie erschien deshalb nirgends, und ein Agent
    // liess sich an ihren Kursen ueberhaupt nicht platzieren.
    forkJoin({
      lti: this.lmsRegistry
        .listRegistrations('lti13')
        .pipe(catchError(() => of([] as LmsRegistration[]))),
      plugin: this.lmsRegistry
        .listRegistrations('ilias_plugin')
        .pipe(catchError(() => of([] as LmsRegistration[]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ lti, plugin }) => {
        // Placeholder rows from an abandoned wizard run have no usable
        // client id — nothing can launch against them, so hide them here
        // (the LTI integration page still lists them for cleanup).
        // Der Filter gilt nur fuer LTI: eine Plugin-Zeile hat nie eine
        // Client-ID und braucht auch keine.
        this.ltiRegistrations = [
          ...lti.filter((r) => r.lti_client_id && r.lti_client_id !== 'pending'),
          ...plugin,
        ];
        this.ltiLoading = false;
        this.markTabsReady();
      });
    this.dozentService
      .listCourses()
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as any[])))
      .subscribe((courses: any[]) => {
        this.ltiCourses = courses
          .filter((c) => c?.id)
          .map((c) => ({ id: String(c.id), course_name: String(c.course_name || c.name || c.id) }))
          .sort((a, b) => a.course_name.localeCompare(b.course_name));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredAgentTutors(): Tutor[] {
    if (!this.agentSearch) return this.agentTutors;
    const q = this.agentSearch.toLowerCase();
    return this.agentTutors.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.visual_name?.toLowerCase().includes(q),
    );
  }

  get pagedAgentTutors(): Tutor[] {
    const rows = this.filteredAgentTutors;
    const maxPage = Math.max(Math.ceil(rows.length / this.agentPageSize) - 1, 0);
    const page = Math.min(this.agentPage, maxPage);
    const start = page * this.agentPageSize;
    return rows.slice(start, start + this.agentPageSize);
  }

  onAgentSearchChange(): void {
    this.agentPage = 0;
  }

  onAgentPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.filteredAgentTutors.length / this.agentPageSize) - 1, 0);
    this.agentPage = Math.max(0, Math.min(page, maxPage));
  }

  onAgentPageSizeChange(size: number): void {
    this.agentPageSize = Math.min(Math.max(size, 1), 100);
    this.agentPage = 0;
  }

  get canSelectMoodleSite(): boolean {
    const payload = this.authService.accessTokenPayload$.value;
    const role = (payload?.role || '').toLowerCase();
    if (['super_admin', 'admin', 'manager'].includes(role)) return true;
    const perms = (payload?.feature_set_permissions || []).map((p) => String(p).toUpperCase());
    return role === 'user' && !perms.includes('DOZENT');
  }

  get visibleLmsSites(): LmsRegistration[] {
    if (this.lmsSites.length <= 1) return this.lmsSites;
    const selected = this.selectedMoodleRegistrationId
      ? this.lmsSites.find((s) => s.id === this.selectedMoodleRegistrationId)
      : null;
    if (selected) return [selected];
    const primary = this.lmsSites.find((s) => s.is_primary);
    return primary ? [primary] : [this.lmsSites[0]];
  }

  moodleSiteTitle(site: LmsRegistration): string {
    const raw = site.moodle_site_url || site.display_name || site.moodle_site_identifier || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '') || site.display_name || '';
    }
  }

  trackTutor = (_: number, t: Tutor) => t.id;

  /** Pull the Moodle plugin sites once + bulk-load placements per
   *  registration so the Schnellkonfig rows can compute per-agent
   *  Moodle-presence + the auto-bucket-id list without each row
   *  firing its own request. Re-derives `autoBucketDataSourceIds`
   *  whenever the data_sources subject also emits (avoids a race
   *  where dataSources resolve after registrations). */
  private loadLmsSites(): void {
    this.lmsSitesLoading = true;
    this.lmsRegistry
      .listMoodleSites()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((sites) => {
          const selectableSites = sites.filter((s) => this.isUsableMoodleSite(s));
          const placementSites = selectableSites;
          this.lmsSites = selectableSites;
          this.resolveSelectedMoodleRegistration();
          this.rebuildAutoBucketDataSourceIds();
          if (placementSites.length === 0) {
            return of([] as LmsPlacement[][]);
          }
          return forkJoin(
            placementSites.map((s) =>
              this.lmsRegistry.listPlacements(s.id).pipe(
                catchError(() => of([] as LmsPlacement[])),
              ),
            ),
          );
        }),
      )
      .subscribe({
        next: (placementLists) => {
          const map = new Map<string, LmsPlacement[]>();
          for (const list of placementLists) {
            for (const p of list) {
              if (!p.bot_id) continue;
              const arr = map.get(p.bot_id) || [];
              arr.push(p);
              map.set(p.bot_id, arr);
            }
          }
          this.placementsByBotId = map;
          this.lmsSitesLoading = false;
        },
        error: () => {
          this.lmsSitesLoading = false;
        },
      });
  }

  private isUsableMoodleSite(site: LmsRegistration): boolean {
    const connectionStatus = String(site.connection_status || '').toLowerCase();
    const healthStatus = String(site.health_status || '').toLowerCase();
    const unusable = ['disconnected', 'paused', 'superseded', 'token_revoked'];
    return (
      !site.disabled_at &&
      !site.ws_token_revoked_at &&
      !unusable.includes(connectionStatus) &&
      !unusable.includes(healthStatus)
    );
  }

  private resolveSelectedMoodleRegistration(): void {
    if (this.lmsSites.length === 0) {
      this.selectedMoodleRegistrationId = null;
      this.lmsRegistry.setStoredMoodleRegistrationId('placements', null);
      return;
    }
    const stored = this.canSelectMoodleSite
      ? this.lmsRegistry.getStoredMoodleRegistrationId('placements')
      : null;
    const storedSite = stored ? this.lmsSites.find((s) => s.id === stored) : null;
    const fallback = this.lmsSites.find((s) => s.is_primary) || this.lmsSites[0];
    this.selectedMoodleRegistrationId = (storedSite || fallback).id;
    if (this.canSelectMoodleSite) {
      this.lmsRegistry.setStoredMoodleRegistrationId('placements', this.selectedMoodleRegistrationId);
    }
  }

  onMoodleSiteChange(registrationId: string): void {
    this.selectedMoodleRegistrationId = registrationId;
    this.lmsRegistry.setStoredMoodleRegistrationId('placements', registrationId);
  }

  /** Re-derive the DataSource-id set that mirrors a registration's
   *  auto_bucket_id. Re-invoked whenever either `lmsSites` or
   *  `agentDataSources` changes — both feeds resolve independently. */
  private rebuildAutoBucketDataSourceIds(): void {
    const autoBucketIds = new Set(
      this.lmsSites
        .map((s) => s.auto_bucket_id)
        .filter((id): id is string => !!id),
    );
    if (autoBucketIds.size === 0 || this.agentDataSources.length === 0) {
      this.autoBucketDataSourceIds = new Set();
      return;
    }
    this.autoBucketDataSourceIds = new Set(
      this.agentDataSources
        .filter(
          (ds) =>
            ds.type === 'bucket' &&
            ds.source_metadata?.bucket_id &&
            autoBucketIds.has(String(ds.source_metadata.bucket_id)),
        )
        .map((ds) => ds.id),
    );
  }

  /** Per-row lookup. Returns an empty list when the tutor isn't
   *  placed in any Moodle course yet — the row uses this to render
   *  the read-only sync badge. */
  placementsForTutor(tutorId: string): LmsPlacement[] {
    return this.placementsByBotId.get(tutorId) || [];
  }

  /** Re-fetch placements whenever the user switches into the
   *  Schnellkonfig tab. Catches the most common stale-data case:
   *  user toggled Kurs-Sync / changed bot_id on the LMS tab and
   *  then expects to see the badge / KB list update without a
   *  full page reload. `loadLmsSites` is idempotent + fast (one
   *  HTTP per registration), so re-running it is cheap. */
  onTabChange(tab: { tabTitle: string }): void {
    const quickLabel = this.t.instant('pages.tutors.placements.quick.tabTitle');
    if (tab.tabTitle !== quickLabel) return;
    this.loadLmsSites();
    // Also refresh the global tutor cache — the Schnellkonfig rows
    // read `tutor.tutor_data_source_ids` directly off it.
    this.tutorService.listTutors().pipe(takeUntil(this.destroy$)).subscribe();
  }

  /** *ngFor identity: stops Angular from recycling a card across a
   * different site object when the array is rebuilt — important for
   * the self-heal flow below, where we want the stale card to be
   * destroyed entirely and a fresh one to mount under the active
   * UUID. */
  trackSiteById(_index: number, site: LmsRegistration): string {
    return site.id;
  }

  /** A child site-card got a 410 `site_superseded` on its courses
   * load — its UUID was a soft-disabled stale registration. Reload
   * the registration list; the new list won't contain the dead
   * UUID, the ngFor (keyed by id) tears down THIS card, and a new
   * card mounts under the active site's UUID. Fully self-healing —
   * the user sees the data they expected without any manual click. */
  onSiteSuperseded(): void {
    this.loadLmsSites();
  }
}
